import { get, post, patch } from '../../helpers/api';

// Pre-approved vehicle catalog (Postgres `vehicle` table) — the make/model/year
// source every driver_vehicle points at. Rows are never deleted; isEligible
// retires one from the driver-facing add-vehicle dropdowns. isXl marks a vehicle
// that can take XL trips (third row, room for six passengers).

// make/model/year are multi-select: several values go out as a repeated key
// (make=Honda&make=Toyota), which the endpoint reads as "any of these". Not a
// comma-joined value — a make or model is free text and may contain a comma.
const appendAll = (params, key, values) => {
  (Array.isArray(values) ? values : [values]).forEach((v) => {
    if (v !== undefined && v !== null && v !== '') params.append(key, v);
  });
};

const getVehicles = async ({
  q,
  isEligible,
  isXl,
  make,
  model,
  year,
  sortBy,
  sortDir,
  offset = 0,
  limit = 25,
} = {}) => {
  const params = new URLSearchParams({ offset, limit });
  if (q) params.set('q', q);
  if (isEligible !== undefined && isEligible !== null) params.set('isEligible', isEligible);
  if (isXl !== undefined && isXl !== null) params.set('isXl', isXl);
  appendAll(params, 'make', make);
  appendAll(params, 'model', model);
  appendAll(params, 'year', year);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortDir) params.set('sortDir', sortDir);
  const { data } = await get(`/admin/vehicles?${params.toString()}`);
  return data; // { data: [...], count }
};

// Distinct make/model/year for the filter dropdowns. Cascades: pass makes to
// scope models + years, models to scope years. Spans retired rows too.
const getFilterOptions = async ({ make, model } = {}) => {
  const params = new URLSearchParams();
  appendAll(params, 'make', make);
  appendAll(params, 'model', model);
  const { data } = await get(`/admin/vehicles/filter-options?${params.toString()}`);
  return data; // { makes, models, years }
};

const getVehicleMakes = async () => {
  const { data } = await get('/admin/vehicles/makes');
  return data.makes;
};

const getVehicleModels = async (make) => {
  const params = new URLSearchParams({ make });
  const { data } = await get(`/admin/vehicles/models?${params.toString()}`);
  return data.models;
};

const getVehicleYears = async (make, model) => {
  const params = new URLSearchParams({ make, model });
  const { data } = await get(`/admin/vehicles/years?${params.toString()}`);
  return data.years;
};

const getVehicleColors = async () => {
  const { data } = await get('/admin/vehicles/colors');
  return data.colors;
};

const getVehicle = async (id) => {
  const { data } = await get(`/admin/vehicles/${id}`);
  return data;
};

const createVehicle = async (input) => {
  const { data } = await post('/admin/vehicles', input);
  return data;
};

const updateVehicle = async (id, input) => {
  const { data } = await patch(`/admin/vehicles/${id}`, input);
  return data;
};

const vehiclesApi = {
  getVehicles,
  getFilterOptions,
  getVehicleMakes,
  getVehicleModels,
  getVehicleYears,
  getVehicleColors,
  getVehicle,
  createVehicle,
  updateVehicle,
};

export default vehiclesApi;
